import {gql} from '@apollo/client';
import React from 'react';

import {AntibodyMapContent} from 'antibodies/antibody-map/AntibodyMap';
import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AdcAntibodyQuery, AdcAntibodyQueryVariables} from 'molbio/adcs/adc-map/__generated/AdcAntibodyQuery';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export const ADC_ANTIBODY_QUERY = gql`
  query AdcAntibodyQuery($adcId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    antibodyDrugConjugate(id: $adcId) {
      ... on AntibodyDrugConjugateNode {
        id
        antibody {
          ... on AntibodyNode {
            id
            currentVersionId
          }
          ...InaccessibleInfoGraphQL
        }
        conjugations {
          id
          conjugationSites {
            domainLabel
            residueIndex
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface AdcMapProps {
  /** The API ID of the ADC. */
  adcId: string;
}

export default function AdcMap({adcId}: AdcMapProps): JSX.Element {
  // TODO(BNCH-176320): Use version state shared with version history selector, like AntibodyMap.
  const result = useBenchlingQuery<AdcAntibodyQuery, AdcAntibodyQueryVariables>(ADC_ANTIBODY_QUERY, {
    variables: {adcId},
  });
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return <span>Error loading antibody</span>;
  }

  const adc = result.data.antibodyDrugConjugate;
  if (isInaccessible(adc)) {
    return <span>ADC is not viewable</span>;
  }
  const antibody = adc.antibody;
  if (isInaccessible(antibody)) {
    return <span>Antibody is not viewable</span>;
  }

  // TODO(BNCH-176755): Support more conjugations.
  assert(adc.conjugations.length === 1, 'ADC should have exactly one conjugation');
  const conjugationSites = adc.conjugations[0].conjugationSites;

  // Keyed by the ADC so that interacting with this map doesn't affect the antibody's own map.
  return (
    <AntibodyMapContent
      antibodyId={antibody.id}
      versionId={antibody.currentVersionId}
      mapStateKey={adcId}
      conjugationSites={conjugationSites}
    />
  );
}
